import { db } from "../firebase/firebaseConfig";
import {
  doc,
  updateDoc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  collection,
  writeBatch,
  runTransaction,
  Timestamp,
  DocumentReference,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { Collections, Artist, Song, OriginalSong } from "../types/type";

/**
 * 새로운 원곡 정보를 생성합니다.
 */
export const createOriginalSong = async (
  originalSongData: Omit<OriginalSong, "id" | "songIdList">
): Promise<string> => {
  try {
    // 새 문서 참조 생성 (자동 ID 생성)
    const originalSongRef = doc(collection(db, Collections.ORIGINAL_SONG));
    const originalSongId = originalSongRef.id;

    // 기본 데이터와 함께 새 문서 생성
    await setDoc(originalSongRef, {
      ...originalSongData,
      id: originalSongId,
      songIdList: [], // 초기에는 빈 배열
      madeAt: Timestamp.now(),
    });

    console.log(`Created original song with ID: ${originalSongId}`);
    return originalSongId;
  } catch (error) {
    console.error("Error creating original song:", error);
    throw error;
  }
};

/**
 * 기존 곡을 원곡과 연결합니다.
 * 트랜잭션을 사용하여 양쪽 데이터의 일관성을 보장합니다.
 */
export const linkSongToOriginal = async (
  songId: string,
  originalSongId: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      // 1. 문서 참조 가져오기
      const songRef = doc(db, Collections.SONG, songId);
      const originalSongRef = doc(
        db,
        Collections.ORIGINAL_SONG,
        originalSongId
      );

      // 2. 문서 존재 확인
      const [songDoc, originalSongDoc] = await Promise.all([
        transaction.get(songRef),
        transaction.get(originalSongRef),
      ]);

      if (!songDoc.exists()) {
        throw new Error(`Song with ID ${songId} does not exist`);
      }
      if (!originalSongDoc.exists()) {
        throw new Error(
          `Original song with ID ${originalSongId} does not exist`
        );
      }

      const originalSongData = originalSongDoc.data() as OriginalSong;

      // 3. songIdList 업데이트 (중복 방지)
      const updatedSongIdList = Array.from(
        new Set([...originalSongData.songIdList, songId])
      );

      // 4. 트랜잭션으로 양쪽 업데이트
      transaction.update(songRef, {
        originalSongId,
        isOriginalSong: false,
      });

      transaction.update(originalSongRef, {
        songIdList: updatedSongIdList,
      });
    });

    console.log(
      `Successfully linked song ${songId} to original song ${originalSongId}`
    );
  } catch (error) {
    console.error("Error linking song to original:", error);
    throw error;
  }
};

/**
 * 원곡 정보를 ID로 조회합니다.
 */
export const getOriginalSongById = async (
  originalSongId: string
): Promise<OriginalSong | null> => {
  try {
    const originalSongRef = doc(db, Collections.ORIGINAL_SONG, originalSongId);
    const originalSongDoc = await getDoc(originalSongRef);

    if (!originalSongDoc.exists()) {
      console.log(`No original song found with ID: ${originalSongId}`);
      return null;
    }

    return originalSongDoc.data() as OriginalSong;
  } catch (error) {
    console.error("Error getting original song:", error);
    throw error;
  }
};

/**
 * 곡과 원곡의 연결을 해제합니다.
 * 트랜잭션을 사용하여 데이터 일관성을 보장합니다.
 */
export const unlinkSongFromOriginal = async (songId: string): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      // 1. Song 문서 가져오기
      const songRef = doc(db, Collections.SONG, songId);
      const songDoc = await transaction.get(songRef);

      if (!songDoc.exists()) {
        throw new Error(`Song with ID ${songId} does not exist`);
      }

      const songData = songDoc.data() as Song;
      const originalSongId = songData.originalSongId;

      if (!originalSongId) {
        console.log(`Song ${songId} is not linked to any original song`);
        return;
      }

      // 2. Original Song 문서 가져오기
      const originalSongRef = doc(
        db,
        Collections.ORIGINAL_SONG,
        originalSongId
      );
      const originalSongDoc = await transaction.get(originalSongRef);

      if (originalSongDoc.exists()) {
        const originalSongData = originalSongDoc.data() as OriginalSong;

        // 3. songIdList에서 제거
        const updatedSongIdList = originalSongData.songIdList.filter(
          (id) => id !== songId
        );

        transaction.update(originalSongRef, {
          songIdList: updatedSongIdList,
        });
      }

      // 4. Song 문서 업데이트
      transaction.update(songRef, {
        originalSongId: "",
        isOriginalSong: false,
      });
    });

    console.log(`Successfully unlinked song ${songId} from original song`);
  } catch (error) {
    console.error("Error unlinking song from original:", error);
    throw error;
  }
};

/**
 * 원곡 정보를 업데이트합니다.
 */
export const updateOriginalSong = async (
  originalSongId: string,
  updateData: Partial<Omit<OriginalSong, "id" | "songIdList">>
): Promise<void> => {
  try {
    const originalSongRef = doc(db, Collections.ORIGINAL_SONG, originalSongId);
    await updateDoc(originalSongRef, updateData);
    console.log(`Successfully updated original song ${originalSongId}`);
  } catch (error) {
    console.error("Error updating original song:", error);
    throw error;
  }
};

/**
 * 원곡에 연결된 모든 곡 정보를 가져옵니다.
 */
export const getLinkedSongs = async (
  originalSongId: string
): Promise<Song[]> => {
  try {
    const songsCollection = collection(db, Collections.SONG);
    const q = query(
      songsCollection,
      where("originalSongId", "==", originalSongId)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(
      (doc) => ({ ...doc.data(), songId: doc.id } as Song)
    );
  } catch (error) {
    console.error("Error getting linked songs:", error);
    throw error;
  }
};

/**
 * 가이드와 원곡을 연결하는 함수
 * @param guideId 가이드 ID
 * @param originalSongId 원곡 ID
 */
export const linkGuideToOriginal = async (
  guideId: string,
  originalSongId: string
): Promise<void> => {
  try {
    // 1. Guide 문서에 originalSongId 필드 업데이트
    const guideRef = doc(db, Collections.GUIDE, guideId);
    await updateDoc(guideRef, {
      originalSongId: originalSongId,
    });

    // 2. OriginalSong 문서에 guideIdList 필드 업데이트 (배열에 guideId 추가)
    const originalSongRef = doc(db, Collections.ORIGINAL_SONG, originalSongId);
    await updateDoc(originalSongRef, {
      guideIdList: arrayUnion(guideId),
    });
  } catch (error) {
    console.error("Error linking guide to original song:", error);
    throw error;
  }
};

/**
 * 가이드와 원곡 연결을 해제하는 함수
 * @param guideId 가이드 ID
 */
export const unlinkGuideFromOriginal = async (
  guideId: string
): Promise<void> => {
  try {
    // 1. 가이드 문서 가져오기
    const guideRef = doc(db, Collections.GUIDE, guideId);
    const guideSnap = await getDoc(guideRef);

    if (!guideSnap.exists()) {
      throw new Error("Guide not found");
    }

    const guideData = guideSnap.data();
    const originalSongId = guideData.originalSongId;

    if (!originalSongId) {
      throw new Error("Guide does not have an original song linked");
    }

    // 2. 원곡 문서에서 guideIdList 배열에서 guideId 제거
    const originalSongRef = doc(db, Collections.ORIGINAL_SONG, originalSongId);
    await updateDoc(originalSongRef, {
      guideIdList: arrayRemove(guideId),
    });

    // 3. 가이드 문서에서 originalSongId 필드 제거
    await updateDoc(guideRef, {
      originalSongId: null,
    });
  } catch (error) {
    console.error("Error unlinking guide from original song:", error);
    throw error;
  }
};
/**
 * 모든 원곡 데이터를 한 번에 가져오는 간단한 함수
 * @returns OriginalSong 배열
 */
export const fetchAllOriginalSongs = async (): Promise<OriginalSong[]> => {
  try {
    console.log("Fetching all original songs...");

    // 원곡 컬렉션 참조
    const originalSongsRef = collection(db, Collections.ORIGINAL_SONG);

    // 모든 문서 가져오기
    const querySnapshot = await getDocs(originalSongsRef);

    const originalSongs: OriginalSong[] = [];

    // 문서를 OriginalSong 객체로 변환
    querySnapshot.forEach((doc) => {
      originalSongs.push({
        ...doc.data(),
        id: doc.id,
      } as OriginalSong);
    });

    console.log(`Fetched ${originalSongs.length} original songs`);
    return originalSongs;
  } catch (error) {
    console.error("Error fetching original songs:", error);
    return [];
  }
};
